import {
    createApp,
    createSSRApp,
} from "vue";
import {
    createRouter,
    createWebHistory,
} from "vue-router";
import {
    applyPublicHeadDescriptor,
    resolvePublicHydrationDecision,
} from "@logscord/public-site/adapters/browser";
import { createPublicBrowserRuntime } from "@logscord/public-site/runtime/browser";
import {
    createVuePublicHydrationBootstrap,
    createVuePublicRouteDataStore,
} from "@logscord/public-site/adapters/vue";
import "./styles/log-ban-preview.scss";
import "./styles/logs-load-more.scss";
import "./styles/log-message-preview.scss";
import "./styles/log-role-preview.scss";
import "./styles/mentions.scss";
import "./styles/app-dock.scss";
import "./styles/home-dashboard.scss";
import "./styles/dashboard-builder.scss";
import "./styles/dashboard-heatmap.scss";
import "./styles/dashboard-visualizations.scss";
import "./styles/moderation-settings.scss";
import "./styles/landing.scss";
import "./styles/public-pages.scss";
import "./styles/vision.scss";
import "./styles/trust.scss";
import "./styles/servers.scss";
import "./styles/team.scss";
import "./styles/permissions-simulator.scss";
import "./styles/bulk-role-permissions.scss";
import "./styles/bulk-role-permissions-modal.scss";
import "./styles/role-picker-dialog.scss";
import "./styles/member-directory.scss";
import "./styles/copy-id-button.scss";
import "./styles/social-graph.scss";
import "./styles/profile.scss";

//  PAGES

import App                                  from "./pages/app.js";
import Home                                 from "./pages/home.js";
import Logs                                 from "./pages/logs.js";
import Sanctions                            from "./pages/sanctions.js";
import Settings                             from "./pages/settings.js";
import LogAccessPolicies                    from "./pages/logAccessPolicies.js";
import Users                                from "./pages/users/users.js";
import MemberDirectory                      from "./pages/memberDirectory.js";
import RoleDetails                          from "./pages/roleDetails.js";
import Channels                             from "./pages/channels.js";
import AllChannels                          from "./pages/allchannels.js";
import Report                               from "./pages/report.js";
import { LogscordBrowserPublicLoaders }     from "./pages/public/publicData.js";
import { registerPublicWebMcp }             from "./publicWebMcp.js";
import { registerServiceWorker }            from "./registerServiceWorker.js";
import {
    AppStateInjectionKey,
    createBrowserAppState,
    isCompatibleAppState,
} from "./appState.js";
import {
    getPublicLocaleFromPath,
} from "./locales/publicLocales.js";
import Graph                                from "./pages/graph.js";
import TopVoice                             from "./pages/topVoice.js";
import DashboardBuilder                     from "./pages/dashboardBuilder.js";
import PermissionsSimulator                 from "./pages/permissionsSimulator/PermissionsSimulator.js";
import BulkRolePermissions                  from "./pages/bulkRolePermissions/BulkRolePermissions.js";
import CommunityEngagement                  from "./pages/tools/communityEngagement.js";
import Tools                                from "./pages/tools.js";
import Profile                              from "./pages/profile.js";
import Features                             from "./pages/public/features.js";
import Vision                               from "./pages/public/vision.js";
import Compare                              from "./pages/public/compare.js";
import Trust                                from "./pages/public/trust.js";
import Servers                              from "./pages/public/servers.js";
import Server                               from "./pages/public/server.js";
import Team                                 from "./pages/public/team.js";
import Docs                                 from "./pages/public/docs.js";
import DocPage                              from "./pages/public/DocPage.js";
import LegalPage                            from "./pages/public/LegalPage.js";
import Intro                                from "./pages/public/intro.js";
import NotFound                             from "./pages/public/404.js";
import type { LogscordPublicComponentId }   from "@logscord/public-site-config";
import type { Component }                   from "vue";
import { PublicSite } from "./publicSite.js";
import { createLogscordHydrationRoutes, createLogscordNotFoundRoute } from "./publicVueRoutes.js";


//////////////////////////////////////////////////
//  ROUTES
//////////////////////////////////////////////////


const PublicComponentById: Record<LogscordPublicComponentId, Component> = {
    intro                     : Intro,
    features                  : Features,
    vision                    : Vision,
    compare                   : Compare,
    trust                     : Trust,
    servers                   : Servers,
    server                    : Server,
    team                      : Team,
    docs                      : Docs,
    doc                       : DocPage,
    legal                     : LegalPage,
    "app-home"                : Home,
    profile                   : Profile,
    logs                      : Logs,
    "dashboard-builder"       : DashboardBuilder,
    sanctions                 : Sanctions,
    settings                  : Settings,
    "log-access-policies"     : LogAccessPolicies,
    "community-engagement"    : CommunityEngagement,
    tools                     : Tools,
    "permissions-simulator"   : PermissionsSimulator,
    "bulk-role-permissions"   : BulkRolePermissions,
    "member-directory"        : MemberDirectory,
    user                      : Users,
    "role-details"            : RoleDetails,
    "all-channels"            : AllChannels,
    channel                   : Channels,
    report                    : Report,
    graph                     : Graph,
    voice                     : TopVoice,
};

const ClientRoutes = createLogscordHydrationRoutes({
    components: PublicComponentById,
});

const router = createRouter({
    history: createWebHistory(),
    routes: [
        ...ClientRoutes,
        createLogscordNotFoundRoute(NotFound),
    ],
});


//////////////////////////////////////////////////
//  MAIN
//////////////////////////////////////////////////


//  APP

const RootSelector = "#logscord-root";
const rootElement = document.querySelector<HTMLElement>(RootSelector);
const hydrationDecision = resolvePublicHydrationDecision({
    rootElement,
    state        : window.__LOGSCORD_APP_STATE__,
    isCompatible: isCompatibleAppState,
    clientBuildId: __LOGSCORD_PUBLIC_BUILD_ID__,
    currentUrl  : `${window.location.pathname}${window.location.search}`,
});
const appState = createBrowserAppState();
const publicRouteData = createVuePublicRouteDataStore(
    hydrationDecision.state?.publicSite.routeData,
);
const publicBrowserRuntime = createPublicBrowserRuntime({
    site                   : PublicSite,
    loaders                : LogscordBrowserPublicLoaders,
    headResolvers          : PublicSite.headResolvers,
    resolveHead            : PublicSite.resolveHead,
    resolveNotFoundHead    : PublicSite.resolveNotFoundHead,
    resolveUnavailableHead : PublicSite.resolveUnavailableHead,
    applyHead              : applyPublicHeadDescriptor,
    initialState           : publicRouteData.snapshot(),
    onRedirect             : (location) => {
        void router.replace(location);
    },
    onError                : (error) => {
        console.error("[PUBLIC SITE] Browser data loader failed.", error);
    },
});

const app = hydrationDecision.mode === "hydrate"
    ? createSSRApp(App)
    : createApp(App);

app.provide(AppStateInjectionKey, appState);
app.use(router);

//  MAIN

const publicBootstrap = createVuePublicHydrationBootstrap({
    router,
    runtime   : publicBrowserRuntime,
    routeData : publicRouteData,
    beforeRoute(to) {
        const routeLocale = getPublicLocaleFromPath(to.path);

        if (to.meta["publicLocalized"] === true && routeLocale) {
            appState.setLocale(routeLocale);
            appState.setLocaleLocked(true);

        } else {
            appState.setLocaleLocked(false);
        }

    },
});

void publicBootstrap.mount(app, RootSelector).then(() => {
    if (localStorage["primaryColor"])
        document.body.style.setProperty("--primary", localStorage["primaryColor"]);

    registerServiceWorker();
    registerPublicWebMcp();
});
